import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {DisplayContentFields} from '@/components/molecules';
import {TargetTypeLabel} from '@/components/atoms';
import {DISPLAY_CONTENT_FIELD_CONFIG as FIELD_CONFIG} from '@/services/schema/displayContentSchema';
import {getDisplayFieldsConfig} from '@/utils/salesTargetDisplayFieldsConfig';

const DisplayContentTab = (props) => {
  const {
    setDisplayContentFormData,
    displayContentFormData,
    activeLanguage,
    languages,
    targetType,
    showAllLanguages = false
  } = props;

  const handleFieldChange = useCallback(
    (langCode, fieldName, value, hasError) => {
      setDisplayContentFormData((prevData) => ({
        ...prevData,
        [langCode]: {
          ...prevData[langCode],
          [fieldName]: value,
          [`${fieldName}Valid`]: hasError
        }
      }));
    },
    [setDisplayContentFormData]
  );

  const config = useMemo(
    () => getDisplayFieldsConfig(targetType),
    [targetType]
  );

  const data = useMemo(
    () => displayContentFormData[activeLanguage] || {},
    [displayContentFormData, activeLanguage]
  );

  const {inputRows, textareaRows} = config?.layout || {
    inputRows: [],
    textareaRows: []
  };

  const fieldDefinitions = config?.fields || [];

  // Fallback if config is not found
  if (!config) {
    return (
      <div data-testid='displayContentForm'>
        <div className='display-content-form'>
          <TargetTypeLabel value={targetType} />
          <div className='text-white'>
            Configuration not found for target type: {targetType}
          </div>
        </div>
      </div>
    );
  }

  const isActive = languages?.some((lang) => lang.code === activeLanguage);

  if (!isActive && !showAllLanguages)
    return <div data-testid='displayContentForm' />;

  return (
    <div data-testid='displayContentForm'>
      <div className='display-content-form'>
        {showAllLanguages ? (
          <div className='grid w-full grid-cols-1 gap-6 md:max-w-[48rem] md:grid-cols-2'>
            {languages?.map((lang) => {
              const langData = displayContentFormData?.[lang.code] || {};
              return (
                <div
                  key={lang.code}
                  className='col-span-1 w-full max-w-full overflow-hidden py-2'>
                  <h3 className='mb-6 border-b border-[color:var(--color-border-divider)] pb-2 text-sm font-semibold text-white'>
                    {lang.title}
                  </h3>

                  <div className='display-content-form'>
                    <DisplayContentFields
                      rows={[...inputRows, ...textareaRows]}
                      fieldDefinitions={fieldDefinitions}
                      langCode={lang.code}
                      langData={langData}
                      fieldConfig={FIELD_CONFIG}
                      handleFieldChange={handleFieldChange}
                      singleColumn
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            <TargetTypeLabel value={targetType} />
            <DisplayContentFields
              rows={[...inputRows, ...textareaRows]}
              fieldDefinitions={fieldDefinitions}
              langCode={activeLanguage}
              langData={data}
              fieldConfig={FIELD_CONFIG}
              handleFieldChange={handleFieldChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

DisplayContentTab.propTypes = {
  setDisplayContentFormData: PropTypes.func.isRequired,
  displayContentFormData: PropTypes.object.isRequired,
  activeLanguage: PropTypes.string,
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string
    })
  ).isRequired,
  targetType: PropTypes.string.isRequired,
  showAllLanguages: PropTypes.bool
};

export default React.memo(DisplayContentTab);
